import { Link, useLocation } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { useState, useEffect } from 'react';

function isActive(pathname, href) {
  if (href === '/') return pathname === '/';
  return pathname.startsWith(href);
}

function hasActiveChild(pathname, item) {
  if (item.href) return isActive(pathname, item.href);
  if (item.children) return item.children.some((child) => hasActiveChild(pathname, child));
  return false;
}

export default function SidebarNavGroup({ item, collapsed, depth = 0 }) {
  const location = useLocation();
  const hasChildren = item.children && item.children.length > 0;
  const active = item.href ? isActive(location.pathname, item.href) : false;
  const childActive = hasChildren && hasActiveChild(location.pathname, item);
  const [open, setOpen] = useState(childActive);

  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  const Icon = item.icon;

  // Leaf node (direct link)
  if (!hasChildren) {
    return (
      <Link
        to={item.href}
        className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
          active
            ? 'bg-blue-600 text-white'
            : 'text-slate-300 hover:bg-slate-700/60 hover:text-white'
        } ${depth > 0 ? 'ml-4' : ''}`}
        title={collapsed ? item.name : undefined}
      >
        {Icon && <Icon className="h-5 w-5 flex-shrink-0" />}
        {!collapsed && <span className="truncate">{item.name}</span>}
      </Link>
    );
  }

  // Collapsed sidebar: show only icon for top-level groups
  if (collapsed && depth === 0) {
    return (
      <div className="relative group">
        <button
          className={`flex items-center justify-center w-full rounded-lg p-2 text-sm font-medium transition-colors ${
            childActive
              ? 'bg-slate-700 text-blue-400'
              : 'text-slate-400 hover:bg-slate-700/60 hover:text-white'
          }`}
          title={item.name}
        >
          {Icon && <Icon className="h-5 w-5" />}
        </button>
        {/* Flyout on hover */}
        <div className="absolute left-full top-0 ml-2 hidden group-hover:block z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-xl py-2 px-1 min-w-[180px]">
            <p className="px-3 py-1 text-xs font-semibold text-slate-400 uppercase tracking-wider">{item.name}</p>
            {item.children.map((child) => (
              <SidebarNavGroup key={child.name} item={child} collapsed={false} depth={1} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Expandable group
  return (
    <div className={depth > 0 ? 'ml-4' : ''}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-3 w-full rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
          childActive
            ? 'text-white'
            : 'text-slate-400 hover:bg-slate-700/60 hover:text-white'
        }`}
      >
        {Icon && <Icon className="h-5 w-5 flex-shrink-0" />}
        <span className="truncate flex-1 text-left">{item.name}</span>
        <ChevronRightIcon
          className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
        />
      </button>
      {open && (
        <div className="mt-0.5 space-y-0.5">
          {item.children.map((child) => (
            <SidebarNavGroup key={child.name} item={child} collapsed={false} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}
